import TurndownService from 'turndown'

const STORAGE_KEY = 'supra-editor-content'
const STORAGE_FILENAME_KEY = 'supra-editor-filename'

/**
 * Result type for conversion operations
 */
export interface ConversionResult {
  success: boolean
  content: string
  error?: string
}

// Initialize Turndown for HTML to Markdown conversion
const turndownService = new TurndownService({
  headingStyle: 'atx',
  codeBlockStyle: 'fenced',
  bulletListMarker: '-',
  emDelimiter: '*',
  strongDelimiter: '**',
})

// Configure Turndown rules for better markdown output
turndownService.addRule('taskList', {
  filter: (node) => {
    return node.nodeName === 'LI' && node.parentElement?.getAttribute('data-type') === 'taskList'
  },
  replacement: (content, node) => {
    const checkbox = (node as HTMLElement).querySelector('input[type="checkbox"]')
    const checked = checkbox?.hasAttribute('checked') || (checkbox as HTMLInputElement)?.checked
    return `- [${checked ? 'x' : ' '}] ${content.trim()}\n`
  },
})

turndownService.addRule('strikethrough', {
  filter: ['s', 'del'] as const,
  replacement: (content) => `~~${content}~~`,
})

turndownService.addRule('highlight', {
  filter: 'mark',
  replacement: (content) => `==${content}==`,
})

/**
 * Convert HTML content to Markdown
 * @param html - The HTML content to convert
 * @returns ConversionResult with success status and converted content
 */
export function htmlToMarkdown(html: string): ConversionResult {
  try {
    if (!html || typeof html !== 'string') {
      return { success: true, content: '' }
    }
    const content = turndownService.turndown(html)
    return { success: true, content }
  } catch (error) {
    console.error('Failed to convert HTML to Markdown:', error)
    // Return a safe fallback - strip HTML tags as basic fallback
    const fallbackContent = html.replace(/<[^>]*>/g, '').trim()
    return {
      success: false,
      content: fallbackContent,
      error: 'Failed to convert HTML to Markdown. Using simplified text fallback.',
    }
  }
}

/**
 * Save content to localStorage
 */
export function saveToLocalStorage(html: string, filename?: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, html)
    if (filename) {
      localStorage.setItem(STORAGE_FILENAME_KEY, filename)
    }
  } catch (error) {
    console.error('Failed to save to localStorage:', error)
  }
}

/**
 * Load content from localStorage
 */
export function loadFromLocalStorage(): { html: string | null; filename: string | null } {
  try {
    const html = localStorage.getItem(STORAGE_KEY)
    const filename = localStorage.getItem(STORAGE_FILENAME_KEY)
    return { html, filename }
  } catch (error) {
    console.error('Failed to load from localStorage:', error)
    return { html: null, filename: null }
  }
}

/**
 * Clear saved content from localStorage
 */
export function clearLocalStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(STORAGE_FILENAME_KEY)
  } catch (error) {
    console.error('Failed to clear localStorage:', error)
  }
}

/**
 * Export content as a .md file download
 * @returns ConversionResult indicating if conversion succeeded
 */
export function exportAsMarkdown(html: string, filename: string = 'document.md'): ConversionResult {
  const result = htmlToMarkdown(html)
  const blob = new Blob([result.content], { type: 'text/markdown;charset=utf-8' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename.endsWith('.md') ? filename : `${filename}.md`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)

  return result
}

/**
 * Export content as HTML file download
 */
export function exportAsHtml(html: string, filename: string = 'document.html'): void {
  const fullHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${filename.replace(/\.(html|md)$/, '')}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 800px; margin: 0 auto; padding: 2rem; line-height: 1.6; }
    h1, h2, h3 { color: #1a202c; }
    code { background: #f4f4f4; padding: 0.2em 0.4em; border-radius: 4px; }
    pre { background: #f4f4f4; padding: 1rem; border-radius: 8px; overflow-x: auto; }
    blockquote { border-left: 4px solid #3b82f6; padding-left: 1rem; margin: 1rem 0; color: #64748b; font-style: italic; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #e2e8f0; padding: 0.75rem; text-align: left; }
    th { background: #f8fafc; }
  </style>
</head>
<body>
${html}
</body>
</html>`

  const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename.endsWith('.html') ? filename : `${filename}.html`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}

/**
 * Convert Markdown to HTML for import
 * Simple markdown parser for basic elements
 * @param markdown - The markdown content to convert
 * @returns ConversionResult with success status and converted content
 */
export function markdownToHtml(markdown: string): ConversionResult {
  try {
    if (!markdown || typeof markdown !== 'string') {
      return { success: true, content: '' }
    }

    let html = markdown

    // Escape HTML entities first
    html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

    // Code blocks (must be before inline code)
    html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
      return `<pre><code class="language-${lang || 'plaintext'}">${code.trim()}</code></pre>`
    })

    // Inline code
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>')

    // Headings
    html = html.replace(/^###### (.+)$/gm, '<h6>$1</h6>')
    html = html.replace(/^##### (.+)$/gm, '<h5>$1</h5>')
    html = html.replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>')
    html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>')
    html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>')

    // Bold and italic
    html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')

    // Strikethrough
    html = html.replace(/~~(.+?)~~/g, '<s>$1</s>')

    // Highlight
    html = html.replace(/==(.+?)==/g, '<mark>$1</mark>')

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

    // Images
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1">')

    // Horizontal rules
    html = html.replace(/^---+$/gm, '<hr>')
    html = html.replace(/^\*\*\*+$/gm, '<hr>')

    // Blockquotes
    html = html.replace(/^&gt; (.+)$/gm, '<blockquote><p>$1</p></blockquote>')

    // Task lists
    html = html.replace(/^- \[x\] (.+)$/gm, '<ul data-type="taskList"><li data-type="taskItem" data-checked="true"><label><input type="checkbox" checked><span></span></label><div><p>$1</p></div></li></ul>')
    html = html.replace(/^- \[ \] (.+)$/gm, '<ul data-type="taskList"><li data-type="taskItem" data-checked="false"><label><input type="checkbox"><span></span></label><div><p>$1</p></div></li></ul>')

    // Unordered lists
    html = html.replace(/^- (.+)$/gm, '<ul><li>$1</li></ul>')
    html = html.replace(/^\* (.+)$/gm, '<ul><li>$1</li></ul>')

    // Ordered lists
    html = html.replace(/^\d+\. (.+)$/gm, '<ol><li>$1</li></ol>')

    // Merge consecutive list items
    html = html.replace(/<\/ul>\n<ul>/g, '\n')
    html = html.replace(/<\/ol>\n<ol>/g, '\n')
    html = html.replace(/<\/ul>\s*<ul data-type="taskList">/g, '')
    html = html.replace(/<\/ul>\s*<ul>/g, '')

    // Paragraphs - wrap remaining lines that aren't already wrapped
    const lines = html.split('\n')
    html = lines.map(line => {
      const trimmed = line.trim()
      if (!trimmed) return ''
      if (trimmed.startsWith('<')) return line
      return `<p>${trimmed}</p>`
    }).filter(line => line).join('\n')

    return { success: true, content: html }
  } catch (error) {
    console.error('Failed to convert Markdown to HTML:', error)
    // Return safely escaped input as fallback
    const fallbackContent = `<p>${markdown.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`
    return {
      success: false,
      content: fallbackContent,
      error: 'Failed to convert Markdown to HTML. Displaying raw content.',
    }
  }
}

/**
 * Import result type including conversion status
 */
export interface ImportResult {
  content: string
  filename: string
  conversionResult: ConversionResult
}

/**
 * Import a markdown file via file input
 */
export function importMarkdownFile(): Promise<ImportResult> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.md,.markdown,.txt'

    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0]
      if (!file) {
        reject(new Error('No file selected'))
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        const markdown = e.target?.result as string
        const conversionResult = markdownToHtml(markdown)
        resolve({
          content: conversionResult.content,
          filename: file.name,
          conversionResult,
        })
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsText(file)
    }

    input.oncancel = () => reject(new Error('File selection cancelled'))
    input.click()
  })
}

/**
 * Debounce function for autosave
 */
export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(() => func(...args), wait)
  }
}
